'use client';

import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { RATING_STEPS } from './review-schema';

/**
 * The Phosphor star fills 87.5 % of its box; the 32 px star node of the design draws a 26 px shape
 * that sits 2 px above the centre of its box, i.e. a 30 px icon lifted by 2 px.
 */
const STAR_ICON = 30;

/**
 * Figma „Rating input“ (154:4546): five 32 px stars 4 px apart – #FFD200 up to the chosen one,
 * #E2E5DF after it – and, 16 px right of them, the level word of the choice in Body/L Bold.
 *
 * It is a native radio group (one `input[type=radio]` per star, the stars are their labels), so the
 * browser brings the roving Tab stop (the chosen star, else the first), the arrow keys that move and
 * choose, and the group semantics. The focus ring is drawn around the star in focus.
 */
export function StarRatingInput({
  name,
  value,
  onChange,
  groupLabel,
  starLabel,
  level,
  invalid,
  describedBy,
  inputRef,
}: {
  name: string;
  /** Chosen stars, 0 = none yet. */
  value: number;
  onChange: (rating: number) => void;
  /** Accessible name of the group. */
  groupLabel: string;
  /** Accessible name of the star for `rating`, e.g. „Hodnotenie 4/5“. */
  starLabel: (rating: number) => string;
  /** Word of the chosen value („Veľmi dobré“); none before a choice. */
  level?: string;
  invalid?: boolean;
  describedBy?: string;
  /** Goes to the first star – react-hook-form focuses it when the rating is invalid. */
  inputRef?: (element: HTMLInputElement | null) => void;
}) {
  return (
    <div className="flex items-center gap-4">
      <div
        role="radiogroup"
        aria-label={groupLabel}
        aria-required="true"
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        className="flex gap-1"
      >
        {RATING_STEPS.map(rating => (
          <label key={rating} className="relative flex size-8 cursor-pointer">
            <input
              ref={rating === 1 ? inputRef : undefined}
              type="radio"
              name={name}
              value={rating}
              checked={value === rating}
              onChange={() => onChange(rating)}
              aria-label={starLabel(rating)}
              className="peer absolute inset-0 size-full cursor-pointer appearance-none opacity-0 outline-none"
            />
            <Icon
              name="star"
              size={STAR_ICON}
              className={cn(
                'pointer-events-none m-auto -translate-y-0.5 transition-colors',
                rating <= value ? 'text-[#FFD200]' : 'text-[#E2E5DF]',
              )}
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 rounded-xl peer-focus-visible:shadow-focus"
            />
          </label>
        ))}
      </div>
      {level ? <span className="nt-body-l-bold text-[#0D110D]">{level}</span> : null}
    </div>
  );
}
